import classNames from 'classnames'
import React from 'react'
import {Link} from 'react-router-dom'
import {MdAccountBalance} from 'react-icons/md'
import {IoCheckmarkCircle, IoTimeOutline} from 'react-icons/io5'
import {GoDotFill} from 'react-icons/go'
import {HiMiniMapPin} from 'react-icons/hi2'
import {BsBoxArrowUpRight} from 'react-icons/bs'
import getLinkToDetail from 'helpers/getLinkToDetail'
import useTranslate from 'modules/local/useTranslate'
import {renderIf} from 'views/Shared'

// Cây phân cấp trực quan (panel bên phải) - liệt kê các cấp TỔ TIÊN rồi tới node CUỐI CÙNG là chính
// tổ chức đang khai báo (luôn được đánh dấu riêng bằng badge "current" - xem CurrentOrgMarker - để
// không lẫn với 1 cấp tổ tiên NGẪU NHIÊN cũng đang "connected").
//
// Trạng thái node tổ tiên: 'connected' (cấp đó ĐÃ có `ownerOrg` - 1 Organization khác được xác thực
// chính thức đại diện quản lý) hoặc 'unmanaged' (cấp đó tồn tại trong danh bạ nhưng CHƯA có tổ chức
// nào đại diện, `ownerOrg=null`). Trạng thái node cuối: 'empty' (chưa khai báo) - 'pending' (đã gửi,
// chờ duyệt) - 'connected' (đã duyệt) - 'rejected' (bị từ chối).
//
// Mỗi node CÓ THỂ bấm được (đi thẳng tới trang tổ chức) khi có nơi để tới: node tổ tiên cần `ownerOrg`,
// node cuối luôn có (chính organization đang xem trang này) - xem `linkTo`/`currentOrgLink` bên dưới.

const STATUS_STYLE = {
    connected: {
        wrap: 'border-green-300 bg-green-50',
        badge: 'bg-white text-green-600 border border-green-300',
        icon: <IoCheckmarkCircle size={13}/>,
        label: 'connected',
    },
    pending: {
        wrap: 'border-amber-300 bg-amber-50',
        badge: 'bg-white text-amber-600 border border-amber-300',
        icon: <IoTimeOutline size={13}/>,
        label: 'pending approval',
    },
    empty: {
        wrap: 'border-dashed border-color-200 background-50',
        badge: 'bg-white text-color-400 border border-color-200',
        icon: <GoDotFill size={9}/>,
        label: 'empty',
    },
    unmanaged: {
        wrap: 'border-dashed border-color-200 background-50',
        badge: 'bg-white text-color-400 border border-color-200',
        icon: <GoDotFill size={9}/>,
        label: 'not yet managed by an organization',
    },
    rejected: {
        wrap: 'border-red-300 bg-red-50',
        badge: 'bg-white text-red-500 border border-red-300',
        icon: <GoDotFill size={9}/>,
        label: 'request rejected',
    },
}

// Badge riêng đánh dấu "đây là tổ chức của bạn" - độc lập với màu trạng thái (connected/pending/...)
// vì 1 cấp tổ tiên cũng có thể "connected" (do 1 org KHÁC quản lý cấp đó), cần phân biệt rõ với chính
// tổ chức đang xem trang này.
const CurrentOrgMarker = () => {
    const t = useTranslate()
    return (
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-500 text-white text-2xs font-bold whitespace-nowrap">
            <HiMiniMapPin size={11}/>
            {t('this is your organization')}
        </span>
    )
}

const TreeNodeCard = ({level, title, name, status = 'connected', icon, isLast, isCurrentOrg, linkTo}) => {
    const t = useTranslate()
    const style = STATUS_STYLE[status] || STATUS_STYLE.connected
    const isNeutral = status === 'empty' || status === 'unmanaged'
    // Cả card là target bấm được (không chỉ dòng tên) - vùng chạm rộng hơn, dễ bấm trên mobile. Chỉ
    // bấm được khi có nơi để đi tới: node tổ tiên cần ownerOrg (tổ chức khác đã xác thực quản lý cấp
    // đó), node cuối (tổ chức của bạn) luôn có (chính là organization đang xem trang này).
    const CardWrapper = linkTo ? Link : 'div'
    const wrapperProps = linkTo ? {to: linkTo} : {}
    return (
        <div className="relative flex gap-3">
            <div className="relative flex flex-col items-center flex-shrink-0" style={{width: 28}}>
                <div
                    className={classNames(
                        'flex flex-center rounded-full border-2 background',
                        isNeutral ? 'border-color-200 text-color-300' : 'border-current',
                        status === 'connected' && 'text-green-500',
                        status === 'pending' && 'text-amber-500',
                        status === 'rejected' && 'text-red-500',
                    )}
                    style={{width: 28, height: 28}}>
                    {icon}
                </div>
                {!isLast && <div className="flex-1 w-0 border-l-2 border-dashed border-color-100 my-0.5"/>}
            </div>
            <CardWrapper
                {...wrapperProps}
                className={classNames(
                    'flex-1 mb-3 p-3 rounded-xl border no-underline',
                    style.wrap,
                    isCurrentOrg && 'ring-2 ring-secondary-400 ring-offset-1',
                    linkTo && 'cursor-pointer hover:shadow-md hover:border-secondary-300 transition-shadow duration-200'
                )}>
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                    <span className="text-2xs font-bold text-color-400 uppercase tracking-widest">
                        {t('level')} {level}: {t(title)}
                    </span>
                    <div className="flex items-center gap-1.5">
                        {renderIf(isCurrentOrg, <CurrentOrgMarker/>)}
                        <span className={classNames('flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-semibold whitespace-nowrap', style.badge)}>
                            {style.icon}
                            {t(style.label)}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className={classNames(
                        'font-semibold leading-snug',
                        isNeutral ? 'text-color-400 italic text-sm' : 'text-color-800 text-sm'
                    )}>
                        {name || t('not declared yet')}
                    </span>
                    {renderIf(linkTo, <BsBoxArrowUpRight size={11} className="flex-shrink-0 text-secondary-400"/>)}
                </div>
            </CardWrapper>
        </div>
    )
}

const AssociationHierarchyTree = ({nodes = [], finalNode, finalIcon, currentOrg}) => {
    const t = useTranslate()
    // currentOrgLink: node cuối (chính tổ chức đang khai báo) luôn bấm được, đi thẳng về trang tổ
    // chức của chính mình - không phụ thuộc ownerOrg như node tổ tiên.
    const currentOrgLink = currentOrg ? getLinkToDetail(currentOrg) : null
    const allNodes = [...nodes, {...finalNode, isFinal: true}]

    return (
        <div className="p-4 rounded-xl border border-color-100 background flex flex-col">
            <div className="flex items-center gap-2 mb-1">
                <MdAccountBalance size={16} className="text-secondary-500"/>
                <span className="font-bold text-sm text-color-800 uppercase tracking-wide">
                    {t('system hierarchy diagram')}
                </span>
            </div>
            <div className="mb-4 text-xs text-color-400 italic leading-relaxed">
                {t('system hierarchy diagram description')}
            </div>

            <div className="flex flex-col">
                {allNodes.map((node, index) => (
                    <TreeNodeCard
                        key={node.key || index}
                        level={index + 1}
                        title={node.label}
                        name={node.name}
                        status={node.isFinal ? (node.status || 'empty') : (node.ownerOrg ? 'connected' : 'unmanaged')}
                        icon={node.isFinal ? finalIcon : <MdAccountBalance size={13}/>}
                        isLast={index === allNodes.length - 1}
                        isCurrentOrg={node.isFinal}
                        linkTo={node.isFinal ? currentOrgLink : (node.ownerOrg ? getLinkToDetail(node.ownerOrg) : null)}
                    />
                ))}
            </div>

            {renderIf(
                finalNode?.status === 'pending',
                <div className="mt-1 flex items-start gap-2 text-2xs text-color-400 italic leading-relaxed">
                    <span>ℹ️</span>
                    <span>{t('approval request reviewed by system admin note')}</span>
                </div>
            )}
        </div>
    )
}

export default AssociationHierarchyTree
